import React, { ReactNode } from 'react';
import { Flex } from 'glints-aries/es';
import { Icon, Typography } from 'glints-aries/es/@next';
import { Blue, Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space4 } from 'glints-aries/es/@next/utilities/spacing';
import { useController, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { CompanyStatusErrorMessages } from '../../../../common/constants';
import { useCompanySearch } from '../../../../common/hooks/requests/companySearch';
import useDebounce from '../../../../common/hooks/useDebounce';
import { useGetSessionCompany } from '../../../../common/hooks/useGetSessionCompany';
import { Company } from '../../../../common/interfaces/entities';
import { ROUTES } from '../../../../common/routes';
import CompanyLegalNameAdditionalDetailsPopover from '../../../CompanyLegalNameAdditionalDetailsPopover';
import { useCompanyVerificationFormContext } from '../FormContext';
import { useCompanyVerificationTracking } from '../hooks/useCompanyVerificationTracking';
import MismatchedFieldWarning from './MismatchedFieldWarning';
import TextField from './TextField';

const defaultCompanyExistsError = 'default_company_exists_error';

function LogoutLink(...chunks: React.ReactNode[]) {
  return (
    <Link to={`/${ROUTES.logout}`}>
      <Typography
        color={Blue.S99}
        as="span"
        style={{
          textDecoration: 'underline',
        }}
      >
        {chunks}
      </Typography>
    </Link>
  );
}

function useDebouncedSearchCompany() {
  const { sessionCompany } = useGetSessionCompany();
  const { control, clearErrors, setError } =
    useCompanyVerificationFormContext();
  const companyName = useWatch({
    control,
    name: 'verifyWithLegalDocuments.companyName',
  });
  const debouncedSearchValue = useDebounce(companyName, 500);

  function getCompanyByName(companies: Company[], name: string) {
    return companies.find(
      company => company.name.toLowerCase() === name.toLowerCase()
    );
  }

  const { companies } = useCompanySearch(debouncedSearchValue, [], {
    skip: sessionCompany?.name === companyName,
    onSuccess: data => {
      const existingCompany = getCompanyByName(data.data, companyName);
      if (!existingCompany) {
        clearErrors('verifyWithLegalDocuments.companyExistsError');
        return;
      }

      setError('verifyWithLegalDocuments.companyExistsError', {
        type: 'manual',
        message: 'Company already exists',
      });
    },
  });
  const existingCompany = companies?.find(
    company => company.name.toLowerCase() === companyName.toLowerCase()
  );

  return {
    companyErrorMessageComponent: existingCompany && (
      <FormattedMessage
        {...CompanyStatusErrorMessages[existingCompany.status]}
        values={{ link: LogoutLink }}
      />
    ),
  };
}

type Props = {
  disabled?: boolean;
};

export default function CompanyLegalNameField({ disabled }: Props) {
  const { formatMessage } = useIntl();
  const { control, setError, setValue, clearErrors } =
    useCompanyVerificationFormContext();
  const [isViewOnly, companyName, extractedCompanyName] = useWatch({
    control,
    name: [
      'isViewOnly',
      'verifyWithLegalDocuments.companyName',
      'verifyWithLegalDocuments.companyNPWPDocMetadata.companyName',
    ],
  });
  const {
    field,
    fieldState: { error },
    formState: {
      errors: { verifyWithLegalDocuments },
    },
  } = useController({
    name: 'verifyWithLegalDocuments.companyName',
    control,
  });

  const { companyErrorMessageComponent } = useDebouncedSearchCompany();
  const companyExistErrorMessage =
    (verifyWithLegalDocuments?.companyExistsError?.message &&
      verifyWithLegalDocuments?.companyExistsError.message !==
        defaultCompanyExistsError &&
      companyErrorMessageComponent) ||
    undefined;
  const errorText =
    error?.message ||
    (companyExistErrorMessage as unknown as string | undefined);

  const showMismatchedWarning = Boolean(
    !disabled &&
      !isViewOnly &&
      extractedCompanyName &&
      extractedCompanyName !== companyName
  );

  const { trackOnFieldValid } = useCompanyVerificationTracking();
  const legalNameOnBlur = trackOnFieldValid(
    'verifyWithLegalDocuments.companyName',
    'verification_company_legal_name_filled'
  );

  return (
    <Flex flexDirection="column" gap={space4}>
      <TextField
        {...field}
        fullWidth={true}
        title={
          <FormattedMessage
            id="text-company-legal-name"
            defaultMessage="Company Legal Name"
          />
        }
        titleSuffix={
          <CompanyLegalNameAdditionalDetailsPopover>
            <Icon
              name="ri-information-line"
              width={20}
              fill={Blue.S99}
              style={{
                marginTop: 7,
              }}
            />
          </CompanyLegalNameAdditionalDetailsPopover>
        }
        isRequired={true}
        placeholder={formatMessage({
          id: 'text-company-legal-name-placeholder',
          defaultMessage: 'Enter your company legal name',
        })}
        fieldName="verifyWithLegalDocuments.companyName"
        errorText={errorText}
        onChange={e => {
          field.onChange(e);
          // error will be cleared once we validate that the company does not exist
          setError('verifyWithLegalDocuments.companyExistsError', {
            type: 'manual',
            message: defaultCompanyExistsError,
          });
        }}
        disabled={disabled}
        onBlur={legalNameOnBlur}
      />
      <If condition={showMismatchedWarning}>
        {extractedCompanyName && (
          <MismatchedFieldWarning
            warningMessage={{
              id: 'text-warning-mismatched-name',
              defaultMessage:
                'This name doesn’t match the name from the uploaded document, which could delay verification.',
            }}
            showWarning={!errorText}
            suggestionMessage={{
              id: 'text-we-found-company-name',
              defaultMessage:
                'We found the legal name {companyName} in your document. Use this for faster verification?',
              values: {
                companyName: extractedCompanyName,
                bold: (text: ReactNode) => (
                  <Typography variant="body2" as="span" color={Neutral.B18}>
                    {text}
                  </Typography>
                ),
              },
            }}
            onClick={() => {
              setValue(
                'verifyWithLegalDocuments.companyName',
                extractedCompanyName
              );
              clearErrors('verifyWithLegalDocuments.companyName');
            }}
          />
        )}
      </If>
    </Flex>
  );
}
